import React, { useEffect, useMemo, useState } from 'react';
import { Navigate } from 'react-router-dom';
import { useAppDispatch, useAppSelector } from '../../store/hooks';
import {
  loginWithCredentials,
  verifySecondFactor,
} from '../../store/slices/authSlice';
import { Logo } from '../../shared/components/logo/Logo';
import styles from './Login.module.scss';

const getGreeting = (): string => {
  const hour = new Date().getHours();
  if (hour >= 5 && hour < 12) {
    return 'Good Morning!';
  } else if (hour >= 12 && hour < 17) {
    return 'Good Afternoon!';
  } else if (hour >= 17 && hour < 21) {
    return 'Good Evening!';
  } else {
    return 'Good Night!';
  }
};

const LoginPage: React.FC = () => {
  const dispatch = useAppDispatch();
  const { loading, error, isAuthenticated, requires2fa, challengeToken, allowedFactors } = useAppSelector(
    (state) => state.auth,
  );
  const [email, setEmail] = useState('');
  const [mpin, setMpin] = useState('');
  const [secondFactorCode, setSecondFactorCode] = useState('');
  const [selectedFactor, setSelectedFactor] = useState('TOTP');

  const activeCredential = mpin;

  const formError = useMemo(() => {
    if (!email.trim() || !activeCredential.trim()) {
      return null;
    }
    return error;
  }, [activeCredential, email, error]);

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!email.trim() || !activeCredential.trim()) {
      return;
    }
    await dispatch(
      loginWithCredentials(
        { email: email.trim(), mpin: mpin.trim() },
      ),
    );
  };

  const handleVerifySecondFactor = async (
    event: React.FormEvent<HTMLFormElement>,
  ) => {
    event.preventDefault();

    if (!challengeToken || !secondFactorCode.trim()) {
      return;
    }

    await dispatch(
      verifySecondFactor({
        challengeToken,
        factorType: selectedFactor,
        code: secondFactorCode.trim(),
      }),
    );
  };

  const factorOptions = useMemo(
    () => (allowedFactors.length ? allowedFactors : ['TOTP']),
    [allowedFactors],
  );

  useEffect(() => {
    setSelectedFactor(factorOptions[0]);
  }, [factorOptions]);

  if (isAuthenticated) {
    return <Navigate to="/dashboard" replace />;
  }

  return (
    <main className={styles.login}>
      <div className={styles.loginSidebar}>
        <div className={styles.loginSidebarInner}>
          <div className={styles.loginSidebarTitle}>{getGreeting()}</div>
          <div className={styles.loginSidebarSubtitle}>Please log in to continue</div>
          {requires2fa ? (
            <form className={styles.loginForm} onSubmit={handleVerifySecondFactor}>
              <div className={styles.challengeTitle}>Verify second factor</div>
              <div className={styles.formHint}>
                Finish AminID sign-in with your configured second factor.
              </div>

              <label className={styles.formLabel} htmlFor="aminid-factor-type">
                Authentication factor
              </label>
              <select
                id="aminid-factor-type"
                className={styles.formInput}
                value={selectedFactor}
                onChange={(event) => setSelectedFactor(event.target.value)}
              >
                {factorOptions.map((factor) => (
                  <option key={factor} value={factor}>
                    {factor === 'TOTP' ? 'Authenticator app' : factor}
                  </option>
                ))}
              </select>

              <label className={styles.formLabel} htmlFor="aminid-second-factor-code">
                Verification code
              </label>
              <input
                id="aminid-second-factor-code"
                type="password"
                className={styles.formInput}
                value={secondFactorCode}
                onChange={(event) => setSecondFactorCode(event.target.value.trim())}
                placeholder="Enter your verification code"
                autoComplete="one-time-code"
                required
              />

              {formError ? <div className={styles.formError}>{formError}</div> : null}

              <button
                className={styles.loginButton}
                type="submit"
                disabled={loading || !secondFactorCode.trim()}
              >
                {loading ? 'Verifying...' : 'Verify and continue'}
              </button>
            </form>
          ) : (
            <form className={styles.loginForm} onSubmit={handleSubmit}>
              <label className={styles.formLabel} htmlFor="aminid-email">
                Email
              </label>
              <input
                id="aminid-email"
                type="email"
                className={styles.formInput}
                value={email}
                onChange={(event) => setEmail(event.target.value)}
                placeholder="admin@aminpay.net"
                autoComplete="username"
                required
              />

              <label className={styles.formLabel} htmlFor="aminid-mpin">
                MPIN
              </label>
              <input
                id="aminid-mpin"
                type="password"
                className={styles.formInput}
                value={mpin}
                onChange={(event) => setMpin(event.target.value.replace(/\D/g, '').slice(0, 6))}
                placeholder="Enter your 4- or 6-digit MPIN"
                autoComplete="one-time-code"
                inputMode="numeric"
                required
              />
              <div className={styles.formHint}>
                Use your enrolled 4- or 6-digit MPIN to access AminID.
              </div>

              {formError ? <div className={styles.formError}>{formError}</div> : null}

              <button
                className={styles.loginButton}
                type="submit"
                disabled={loading || !email.trim() || !activeCredential.trim()}
              >
                {loading ? 'Signing in...' : 'Sign In'}
              </button>
            </form>
          )}
        </div>

        <div className={styles.copyright}>
          © 2023-2026 by{' '}
          <a
            href='https://www.iit.af'
            target='_blank'
            rel='noopener noreferrer'
            className={styles.companyLink}
          >
            Identity Infotech
          </a>
          . All Rights Reserved.
        </div>
      </div>
      <div className={styles.loginContent}>
        <Logo withText />
      </div>
    </main>
  );
};

export default LoginPage;
